// Frontend-Zugriff auf den hierarchischen Branchen-Katalog (Backend-Route
// `contact-branche-classification`). Reine Helfer (unit-testbar) + ein
// modulweit gecachter Lade-Hook fürs BrancheSelect-Widget und die Anzeige.

import * as React from "react";
import { apiUrl } from "../utils/api";

/** Ein Ebene-3-Blatt des Branchen-Katalogs (entspricht der Backend-Entity). */
export interface BrancheLeaf {
  id: number;
  ebene1: string;
  ebene2: string;
  ebene3: string;
  erlaeuterung?: string | null;
  grundrisiken?: string | null;
  sortIndex?: number;
  active?: boolean;
}

/** Nur aktive Blätter, in Katalog-Reihenfolge. */
export function activeLeaves(rows: BrancheLeaf[]): BrancheLeaf[] {
  return rows.filter((r) => r.active !== false);
}

/**
 * Blätter, die eine Maske anbieten darf: alle aktiven PLUS die, die diese Firma
 * bereits gespeichert hat.
 *
 * WARUM die zweite Hälfte: ein deaktivierter Eintrag verschwindet aus der
 * Auswahl — an einer Firma, die ihn trägt, aber nicht. Böte die Maske ihn nicht
 * an, stünde er nicht mehr in der Liste, und der nächste Speichervorgang
 * schickte die Auswahl ohne ihn: die Firma verlöre ihre Branche, ohne dass
 * jemand sie angefasst hätte.
 */
export function waehlbareLeaves(
  rows: BrancheLeaf[],
  gewaehlt: Iterable<number> = [],
): BrancheLeaf[] {
  const ids = new Set(gewaehlt);
  return rows.filter((r) => r.active !== false || ids.has(r.id));
}

/** Ist das Blatt deaktiviert (und damit nur noch als Bestandswert sichtbar)? */
export function istDeaktiviert(leaf: BrancheLeaf | undefined): boolean {
  return leaf?.active === false;
}

/**
 * Distinct Ebene-1-Branchen in Katalog-Reihenfolge. `gewaehlt` hält die Branche
 * eines bereits gespeicherten (deaktivierten) Blattes in der Liste — sonst
 * stünde das Branchen-Feld der Firma leer da.
 */
export function distinctEbene1(
  rows: BrancheLeaf[],
  gewaehlt: Iterable<number> = [],
): string[] {
  const out: string[] = [];
  for (const r of waehlbareLeaves(rows, gewaehlt)) {
    if (!out.includes(r.ebene1)) out.push(r.ebene1);
  }
  return out;
}

/** Distinct Ebene-2-Branchengruppen innerhalb einer Ebene 1, in Reihenfolge. */
export function ebene2ForEbene1(rows: BrancheLeaf[], ebene1: string): string[] {
  const out: string[] = [];
  for (const r of activeLeaves(rows)) {
    if (r.ebene1 !== ebene1) continue;
    if (!out.includes(r.ebene2)) out.push(r.ebene2);
  }
  return out;
}

/** Ebene-3-Spezialisierungen (Blätter) innerhalb E1/E2, in Reihenfolge. */
export function leavesFor(
  rows: BrancheLeaf[],
  ebene1: string,
  ebene2: string,
): BrancheLeaf[] {
  return activeLeaves(rows).filter(
    (r) => r.ebene1 === ebene1 && r.ebene2 === ebene2,
  );
}

/** Blatt per id finden (auch inaktive, damit Bestandswerte anzeigbar bleiben). */
export function findLeaf(
  rows: BrancheLeaf[],
  id: number | null | undefined,
): BrancheLeaf | undefined {
  if (id == null) return undefined;
  return rows.find((r) => r.id === id);
}

/** Anzeigepfad „Ebene 1 / Ebene 2 / Ebene 3". */
export function formatBranchePath(leaf: BrancheLeaf | undefined): string {
  if (!leaf) return "";
  return [leaf.ebene1, leaf.ebene2, leaf.ebene3].filter(Boolean).join(" / ");
}

/** Normalisiert Suchtext (kleinschreiben, diakritische Zeichen entfernen). */
function normSearch(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/**
 * Flache Volltextsuche über alle Ebenen (für den Such-Modus des Dropdowns).
 * Alle Tokens müssen im Pfad „E1 E2 E3" vorkommen (UND-Verknüpfung).
 */
export function searchLeaves(
  rows: BrancheLeaf[],
  query: string,
  limit = 50,
): BrancheLeaf[] {
  const q = normSearch(query.trim());
  if (!q) return [];
  const tokens = q.split(/\s+/).filter(Boolean);
  return activeLeaves(rows)
    .filter((r) => {
      const hay = normSearch(`${r.ebene1} ${r.ebene2} ${r.ebene3}`);
      return tokens.every((t) => hay.includes(t));
    })
    .slice(0, limit);
}

// --- modulweit gecachter Loader -------------------------------------------

let cache: BrancheLeaf[] | null = null;
let inflight: Promise<BrancheLeaf[]> | null = null;

/**
 * Wirft den modulweiten Cache weg. Die Katalogpflege in den Einstellungen ruft
 * das nach jeder Änderung: sonst zeigten Anlage und Akte bis zum nächsten
 * Seiten-Reload noch den alten Katalog — inklusive gerade deaktivierter Blätter.
 */
export function invalidateBrancheClassification(): void {
  cache = null;
  inflight = null;
}

async function fetchLeaves(): Promise<BrancheLeaf[]> {
  if (cache) return cache;
  if (!inflight) {
    inflight = fetch(apiUrl("contact-branche-classification"))
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        cache = Array.isArray(d) ? (d as BrancheLeaf[]) : [];
        return cache;
      })
      .catch(() => {
        cache = [];
        return cache;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** Lädt den Katalog einmalig (modulweit gecacht) und liefert Zeilen + Ladezustand. */
export function useBrancheClassification(): {
  rows: BrancheLeaf[];
  loading: boolean;
} {
  const [rows, setRows] = React.useState<BrancheLeaf[]>(cache ?? []);
  const [loading, setLoading] = React.useState<boolean>(cache == null);

  React.useEffect(() => {
    let alive = true;
    if (cache) {
      setRows(cache);
      setLoading(false);
      return;
    }
    fetchLeaves().then((r) => {
      if (!alive) return;
      setRows(r);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  return { rows, loading };
}
